import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useStatusBar } from "./StatusBarContext";
import { v4 as uuidv4 } from "uuid";
import {
  matchOrganization,
  organisationsMock,
  preprodOrganizationsMock,
} from "@/utils/bankingSyndicatesHelper";
import { AnswerRequest, postAnswerAnswerPost } from "@/services";
import { getLoanKeysToDisplay, getLoanStatus, loanToLoanToSend } from "@/utils/loanStatusUtils";
import { altgency_base_url, api_base_url } from "@/App";
import { loanMock } from "./mock";
import { iframeService } from "@/services/iframe.service";
import { organizationControllerFindMany } from "@/altgency_api";
import { client as altgencyClient } from "@/altgency_api/client.gen";
export type ChunkMetadata = {
  title: string;
};

export type Chunk = {
  chunk: string;
  quote?: string;
  metadata?: ChunkMetadata;
};

export type RetrievedLoanInformation = {
  value: any;
  chunk?: Chunk;
  question?: string;
  validated?: boolean;
};

export type Address = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  line1: string;
  line2: string | null;
  line3: string | null;
  cityId: string;
  city: {
    id: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    countryId: string;
    postalCode: string;
    country: {
      name: string;
    };
  };
};

export type Organization = {
  id: string;
  name: string;
  shortName: string;
  registration: string;
  address?: Address | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  industry: string;
  type: string;
  private: boolean;
  addressId?: string | null;
};

export type BankingSyndicate = {
  name: string;
  registration_number: string;
  matched_organisations: Organization[];
  organization?: Organization | null;
  commitment: number;
};

export type MaturityDate = {
  rule?: string;
  reference_date?: string;
  reference_date_key?: 'first_payment_date' | 'delivery_date' | 'signing_date' | 'other';
  maturity_date?: string;
}

export type RetrievedMaturityDate = {
  value: MaturityDate;
  chunk?: Chunk;
  question?: string;
  validated?: boolean;
};

export type Agent = {
  name: string;
  registration_number: string;
  matched_organisations: Organization[];
  organization?: Organization | null;
};

export const LoanType = {
  TERM_LOAN: "TERM_LOAN",
  REVOLVING_CREDIT: "REVOLVING_CREDIT",
  SENIOR_TERM_LOAN: "SENIOR_TERM_LOAN",
  JUNIOR_TERM_LOAN: "JUNIOR_TERM_LOAN",
  JUNIOR_TERM_LOAN_SUB_LOAN_FTL: "JUNIOR_TERM_LOAN_SUB_LOAN_FTL",
  EXPORT_CREDIT_FACILITY: "EXPORT_CREDIT_FACILITY",
  UNSECURED_CORPORATE_LOAN: "UNSECURED_CORPORATE_LOAN",
  BRIDGE_LOAN: "BRIDGE_LOAN",
  UNITRANCHE: "UNITRANCHE",
  OTHERS: "OTHERS",
  TAX_LOAN: "TAX_LOAN",
};

export type LoanType = (typeof LoanType)[keyof typeof LoanType];

export type Vessel = {
  name?: string;
  registration_number?: string;
  deliveryDate?: string;
};

export type Contact = {
  name?: string;
  email?: string;
  role?: string;
  organization?: string;
};

export type Amount = {
  amount: RetrievedLoanInformation;
  currency: RetrievedLoanInformation;
};

export type LoanManagementRule = {
  rule: string;
  field: string;
  triggers: string[];
};

export type Loan = {
  id: string;
  parentId?: string;
  documentUrl: string;
  file?: File;
  documentType: "pdf" | "docx";
  name: string;
  description?: string;
  financed_boat_number?: number;
  type?: LoanType;
  vessels?: RetrievedLoanInformation;
  contacts?: RetrievedLoanInformation;
  amount?: Amount;
  borrower?: RetrievedLoanInformation;
  guarantors?: RetrievedLoanInformation;
  margin?: RetrievedLoanInformation;
  agent?: RetrievedLoanInformation;
  maturity_date?: RetrievedMaturityDate;
  banking_syndicate?: RetrievedLoanInformation;
  tranches?: Loan[];
  LoanManagementRules?: LoanManagementRule[];
};

export const LoanKeys = [
  "amount",
  "borrower",
  "agent",
  "guarantors",
  "margin",
  "maturity_date",
  "banking_syndicate",
  "vessels",
  "contacts",
] as const;
export type LoanKey = (typeof LoanKeys)[number];
type Deal = {
  name: string;
  group?: string;
  reference?: string;
  amount?: number;
  currencyId?: string;
  company?: string;
  loans: Loan[];
  agent?: Organization;
};

type DealContextType = {
  deal: Deal;
  isStepValid: boolean;
  docError: string[];
  drawerOpen: boolean;
  organizations: Organization[];
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDocError: React.Dispatch<React.SetStateAction<string[]>>;
  setIsStepValid: React.Dispatch<React.SetStateAction<boolean>>;
  submitFunction: () => void;
  setSubmitFunction: React.Dispatch<React.SetStateAction<() => void>>;
  setDealName: (name: string) => void;
  addLoan: (loan: Loan) => void;
  uploadFiles: (files: File[]) => void;
  updateLoan: (loanId: string, loanData: Partial<Loan>) => void;
  answerQuestion: (loanId: string, question: string, key: string, answer: string) => Promise<void>;
};

const DealContext = createContext<DealContextType | undefined>(undefined);

const initialDeal: Deal = {
  name: "",
  loans: [],
};

export const DealProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deal, setDeal] = useState<Deal>(initialDeal);
  const [isStepValid, setIsStepValid] = useState(false);
  const [docError, setDocError] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>(preprodOrganizationsMock);
  const [submitFunction, setSubmitFunction] = useState<() => void>(() => {
    null;
  });
  const [datesToSet, setDatesToSet] = useState<{ loanId: string; date: string; key: string }[]>([]);
  const navigate = useNavigate();
  const { addTask, updateTask, removeTask } = useStatusBar();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const setDealName = useCallback((name: string) => {
    setDeal((prevDeal) => ({ ...prevDeal, name, reference: name }));
  }, []);
  const location = useLocation();
  const currentPathRef = useRef(location.pathname);
  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    console.log(altgencyClient.getConfig());
    altgencyClient.setConfig({
      baseUrl: altgency_base_url,
      credentials: "include",
    });
    altgencyClient
      .get<Organization[]>({
        url: "/organizations",
        query: {
          type: "LENDER",
        },
      })
      .then((response) => {
        console.log("Organizations fetched:", response.data);
        if (response.data && Array.isArray(response.data))
          setOrganizations(
            response.data.map((org: any) => ({
              ...org,
              private: org.private ?? false,
            }))
          );
      })
      .catch((error) => {
        console.error("Error fetching organizations:", error);
      });
  }, []);

  useEffect(() => {
    if (!deal.name) {
      navigate("/");
    }
    if (deal.loans.length === 0 && location.pathname.indexOf("/review") > -1) {
      navigate("/upload");
    }
  }, [deal, navigate, location.pathname]);

  const addLoan = (loan: Loan) => {
    setDeal((prevDeal) => ({
      ...prevDeal,
      loans: [...prevDeal.loans.filter((oldloan) => oldloan.id !== loan.id), loan],
    }));
  };

  const sendChanges = (dealToComplete: Deal, data: Partial<Loan>): Deal => {
    if (data.borrower && !dealToComplete.company) {
      dealToComplete = {
        ...dealToComplete,
        company: data.borrower.value,
        group: data.borrower.value,
      };
    }
    if (data.agent && data.agent.value && data.agent.value.organization && data.agent.validated && !dealToComplete.agent) {
      if (data.agent.value.organization && data.agent.value.organization.id) {
        dealToComplete.agent = {
          id: data.agent.value.organization.id,
          name: data.agent.value.organization.name,
          shortName: data.agent.value.organization.shortName,
          registration: data.agent.value.organization.registration,
          address: data.agent.value.organization.address,
          industry: data.agent.value.organization.industry,
          type: data.agent.value.organization.type,
          private: data.agent.value.organization.private,
        };
      }
    }
    if (data.amount && data.amount.amount.value && !isNaN(parseFloat(data.amount.amount.value))) {
      dealToComplete = {
        ...dealToComplete,
        amount: dealToComplete.loans
          .filter((l) => !l.parentId)
          .reduce((acc, loan) => {
            const loanAmount = parseFloat(loan.amount?.amount.value || "0");
            return acc + loanAmount;
          }, 0),
        currencyId: data.amount.currency.value,
      };
    }
    if (
      dealToComplete &&
      dealToComplete.amount &&
      dealToComplete.currencyId &&
      dealToComplete.group
    ) {
      const dealLoans = dealToComplete.loans.map((loan) => loanToLoanToSend(loan, datesToSet));
      const { loans, ...dealWithoutLoans } = dealToComplete;
      iframeService.sendMessage("updateDeal", { loans: dealLoans, ...dealWithoutLoans });
    }
    return dealToComplete;
  };

  const updateLoan = (loanId: string, loanData: Partial<Loan>) => {
    setDeal((prevDeal) => {
      const loanIndex = prevDeal.loans.findIndex((loan) => loan.id === loanId);
      if (loanIndex === -1) return prevDeal; // Loan non trouvé, on ne change rien
      const updatedLoans = [...prevDeal.loans];
      const newLoans: Loan[] = [];
      if (loanData.tranches) {
        console.log("Updating tranches for loan", loanId, loanData.tranches);
        console.log(prevDeal.loans);
        loanData.tranches.forEach((tranche) => {
          const trancheId = tranche.name.replace(/[^a-zA-Z0-9._-]/g, "") + loanId;
          const trancheIndex = prevDeal.loans.findIndex((loan) => loan.id === trancheId);
          const newLoan = {
            ...tranche,
            id: trancheId,
            parentId: loanId,
            documentUrl: prevDeal.loans[loanIndex].documentUrl,
            documentType: prevDeal.loans[loanIndex].documentType,
          };
          tranche = newLoan;
          if (trancheIndex === -1) {
            console.log("Adding new tranche loan", newLoan);
            newLoans.push(newLoan);
          } else {
            console.log("Updating existing tranche loan", newLoan);
            const newEntries = Object.fromEntries(
              Object.entries(newLoan).filter(
                ([key]) =>
                  !(key in updatedLoans[trancheIndex]) ||
                  !updatedLoans[trancheIndex][key as keyof Loan]
              )
            );
            console.log("New entries for tranche", newEntries);
            if (newEntries.banking_syndicate) {
              proccessSyndicate(newEntries);
            }
            if (newEntries.agent) {
              proccessAgent(newEntries);
            }
            if (newEntries.maturity_date) {
              processMaturityDate(newEntries);
            }
            const updatedLoan = {
              ...updatedLoans[trancheIndex],
              ...newEntries,
            };
            updatedLoans[trancheIndex] = updatedLoan;
          }
        });
      }
      updatedLoans[loanIndex] = { ...updatedLoans[loanIndex], ...loanData };
      prevDeal = sendChanges(prevDeal, loanData);
      return { ...prevDeal, loans: [...updatedLoans, ...newLoans] };
    });
  };

  const completeLoan = (loanId: string) => {
    setDeal((prevDeal) => {
      const loanIndex = prevDeal.loans.findIndex((loan) => loan.id === loanId);
      if (loanIndex === -1) return prevDeal; // Loan non trouvé, on ne change rien

      const updatedLoans = [...prevDeal.loans];
      const loan = updatedLoans[loanIndex];
      getLoanKeysToDisplay(loan).forEach((key) => {
        if (!loan[key]) {
          loan[key] = loanMock[key] as any;
        }
      });
      updatedLoans[loanIndex] = loan;

      return { ...prevDeal, loans: updatedLoans };
    });
  };

  const dealRef = useRef<Deal>(deal);

  useEffect(() => {
    dealRef.current = deal;
  }, [deal]);

  const getLoanToDisoplay = () => {
    const currentDeal = dealRef.current;
    if (currentDeal.loans.length === 0) return null;

    const isLoanValidated = (loan: Loan) => {
      return getLoanKeysToDisplay(loan).every((key) => {
        const { status } = getLoanStatus(loan[key]);
        return status === "Validated";
      });
    };

    const notValidatedLoans = currentDeal.loans.filter((loan) => !isLoanValidated(loan));
    return notValidatedLoans.length > 0 ? notValidatedLoans[0] : currentDeal.loans[0];
  };

  const proccessSyndicate = (filteredLoanData: Partial<Loan>) => {
    const bankingSyndicates = (filteredLoanData.banking_syndicate as RetrievedLoanInformation)
      .value;
    const matchedSyndicates = bankingSyndicates.map(
      (syndicate: { name: string; commitment: string, registration_number: string }) => {
        const matchedOrganization = matchOrganization(syndicate.name, organizations, syndicate.registration_number);
        return {
          ...syndicate,
          matched_organisations: matchedOrganization,
        };
      }
    );
    (filteredLoanData.banking_syndicate as RetrievedLoanInformation).value = matchedSyndicates;
    return filteredLoanData;
  };

  const proccessAgent = (filteredLoanData: Partial<Loan>) => {
    const bankingAgent = (filteredLoanData.agent as RetrievedLoanInformation)
      .value;
    const agentWithOrganizations = {
      ...bankingAgent,
      matched_organisations: matchOrganization(bankingAgent.name, organizations, bankingAgent.registration_number),
    };
    (filteredLoanData.agent as RetrievedLoanInformation).value = agentWithOrganizations;
    return filteredLoanData;
  };

  const processMaturityDate = (filteredLoanData: Partial<Loan>) => {
    const maturityDate = (filteredLoanData.maturity_date as RetrievedMaturityDate);
    if (!maturityDate) return filteredLoanData;
    console.log("Processing maturity date", maturityDate);
    console.log(maturityDate.value)
    try {
      (filteredLoanData.maturity_date as RetrievedMaturityDate).value = JSON.parse(maturityDate.value as string);
      return filteredLoanData;
    } catch (error) {
      console.error("Error parsing maturity date:", error);
      return filteredLoanData.maturity_date;
    }
  };

  async function processLoanStreaming({
    loanId,
    file,
    getLocation,
  }: {
    loanId: string;
    file: File;
    getLocation: () => string;
  }) {
    const loan: any = {};
    const formData = new FormData();
    formData.append("loanId", loanId);
    formData.append("file", file);
    const storedKey = localStorage.getItem("apiKey");
    const response = await fetch(`${api_base_url}/process-loan`, {
      method: "POST",
      body: formData,
      headers: {
        "x-api-key": storedKey || "",
      },
    });

    if (!response.ok || !response.body) {
      throw new Error("Erreur lors de la récupération du flux");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const parsed = JSON.parse(line);
          if (parsed.type === "error" && dealRef.current.loans.find((loan) => loan.id === loanId)) {
            if (dealRef.current.loans.find((loan) => loan.id === loanId)?.description) {
              continue;
            }
          }
          if (parsed.type === "task" || parsed.type === "error") {
            removeTask(loanId);
            addTask({ id: loanId, message: parsed.message, progress: "indeterminate" });
            if (
              parsed.message.indexOf("Flow has been manually stopped") > -1 ||
              parsed.type === "error"
            ) {
              let docName: string | undefined = undefined;
              setDeal((prevDeal) => {
                docName = prevDeal.loans.find((loan) => loan.id === loanId)?.name;
                if (docName) {
                  setDocError((prev) => [...prev, docName as string]);
                }
                return {
                  ...prevDeal,
                  loans: prevDeal.loans.filter((loan) => loan.id !== loanId),
                };
              });
            } else if (
              parsed.message.indexOf("Document processing and saving in database") == -1 &&
              parsed.message.indexOf("Processing...") == -1 &&
              getLocation().includes("/upload")
            ) {
              console.log("processing loan", parsed.message);
            }
          } else if (parsed.type === "loan") {
            if (getLocation().includes("/upload")) {
              if (getLoanToDisoplay() == null) {
                navigate("/review");
              }
              const loanToDisplay = getLoanToDisoplay();
              if (loanToDisplay) {
                navigate(`/review/loan/${loanToDisplay.id}`);
              }
            }
            const loadData = parsed.message;
            const filteredLoanData = Object.fromEntries(
              Object.entries(loadData).filter(([_, value]) => value !== null && value !== undefined)
            );
            if (filteredLoanData.banking_syndicate) {
              proccessSyndicate(filteredLoanData);
            }
            if (filteredLoanData.agent) {
              proccessAgent(filteredLoanData);
            }
            if (filteredLoanData.maturity_date) {
              processMaturityDate(filteredLoanData);
            }
            updateLoan(loanId, filteredLoanData);
          }
        } catch (err) {
          console.error("Erreur de parsing JSON", err);
        }
      }
    }
    removeTask(loanId);
    completeLoan(loanId);
    dealRef.current.loans
      .filter((loan) => loan.parentId === loanId)
      .forEach((childLoan) => {
        completeLoan(childLoan.id);
      });
    console.log("Fin du flux.");
  }

  const uploadFiles = async (files: File[]) => {
    for (const file of files) {
      const loanId = uuidv4();
      // const loanId = file.name.split(".")[0]
      //   .split(".")[0]
      //   .replace(/[^a-zA-Z0-9._-]/g, "")
      //   .replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "")
      //   .slice(0, 63);
      addTask({ id: loanId, message: `Upload ${file.name}...`, progress: "indeterminate" });
      addLoan({
        name: file.name.split(".")[0].slice(0, 45),
        id: loanId,
        documentUrl: URL.createObjectURL(file),
        file: file,
        documentType:
          file.type === "application/pdf"
            ? "pdf"
            : file.type ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              ? "docx"
              : (() => {
                  throw new Error("Unsupported file type");
                })(),
      });
      await new Promise((resolve) => setTimeout(resolve, 100));
      processLoanStreaming({ loanId, file, getLocation: () => currentPathRef.current });
    }
  };

  const answerQuestion = async (loanId: string, question: string, key: string, answer: string) => {
    const taskId = uuidv4();
    addTask({
      id: taskId,
      message: `The model is thinking ${question}...`,
      progress: "indeterminate",
    });
    const str_output = `
      The final information you can now provide as a simple string.
      Be precise and concise, return only the information.
    `;
    const body: AnswerRequest = {
      question,
      answer,
      key: key.replaceAll("_", " "),
      output: str_output,
    };
    const loan = deal.loans.find((loan) => loan.id === loanId);
    let ouput_parser: (value: any, chunk?: any) => any = (value: any, chunk?: any) => ({
      value,
      chunk,
    });

    if (key === "maturity_date") {
      body.output = `
        The computed final maturity date as a Datestring in the format MM/dd/yyyy.
        Return only the date without any other text or explanation.
      `;
      if (loan?.maturity_date?.value?.reference_date_key) {
        if (loan?.maturity_date?.value?.reference_date_key !== "other") {
          setDatesToSet((prev) => [
            ...prev,
            { loanId, date: answer, key: loan?.maturity_date?.value?.reference_date_key as string},
          ]);
        }
      }
      ouput_parser = (value: any, chunk?: any) => {
        return {
          value: {
            ...loan?.maturity_date?.value,
            maturity_date: value,
          },
          chunk,
        };
      };
    } else if (key === "vessel") {
      body.output = `
        A json object with the following keys and without any other ponctuation, text or explanation:
        {
          "name": "the name of the vessel",
          "registration_number": "the registration number of the vessel"
        }
      `;
      ouput_parser = (value: any, chunk?: any) => {
        const parsedValue = JSON.parse(value);
        return {
          name: {
            value: parsedValue.name,
            chunk: chunk,
          },
          registration_number: {
            value: parsedValue.registration_number,
            chunk: chunk,
          },
        };
      };
    } else if (key === "amount") {
      body.output = `
        A json object with the following keys and without any other ponctuation, text or explanation:
        {
          "amount": "the amount of the loan",
          "currency": "the currency of the loan"
        }
      `;
      ouput_parser = (value: any, chunk?: any) => {
        const parsedValue = JSON.parse(value);
        return {
          amount: {
            value: parsedValue.amount,
            chunk: chunk,
          },
          currency: {
            value: ["EUR", "USD"].indexOf(parsedValue.currency) > 0 ? parsedValue.currency : "EUR",
            chunk: chunk,
          },
        };
      };
    } else body.output = str_output;

    
    const retrievedInformation = loan
      ? key === "amount"
        ? loan.amount?.amount
        : (loan[key as keyof Loan] as RetrievedLoanInformation)
      : undefined;
    const chunk = retrievedInformation ? retrievedInformation.chunk : undefined;
    const partial_information =
      retrievedInformation && retrievedInformation.value
        ? retrievedInformation.value
        : chunk?.chunk;
    if (
      partial_information &&
      typeof partial_information === "string" &&
      partial_information.length > 0
    ) {
      body.partial_information = `Partial information you already found about ${key.replaceAll("_", " ")}: ${partial_information}`;
    }
    if (partial_information && key === "maturity_date" && partial_information.rule) {
      body.partial_information = `Partial information you already found about ${key.replaceAll("_", " ")}: ${partial_information.rule}`;
    }
    if (chunk) {
      body.chunk =
        JSON.stringify(chunk.chunk) +
        "\n\n" +
        ((loan?.[key as keyof Loan] as RetrievedLoanInformation)?.value ?? "");
    }
    postAnswerAnswerPost({
      body,
    }).then((response: any) => {
      removeTask(taskId);
      const newValue = response.data;
      setIsStepValid(true);
      const newChunk = chunk?.chunk || "";
      updateLoan(loanId, {
        [key]: ouput_parser(newValue, {
          ...(chunk || {}),
          page_content: newChunk + "\n\n Model:" + question + "   \n You:" + answer,
        }),
      });
    });
  };

  return (
    <DealContext.Provider
      value={{
        deal,
        isStepValid,
        docError,
        drawerOpen,
        organizations,
        setDrawerOpen,
        setDocError,
        setIsStepValid,
        submitFunction,
        setSubmitFunction,
        setDealName,
        addLoan,
        uploadFiles,
        updateLoan,
        answerQuestion,
      }}
    >
      {children}
    </DealContext.Provider>
  );
};

export const useDealContext = (): DealContextType => {
  const context = useContext(DealContext);
  if (!context) {
    throw new Error("useDealContext must be used within a DealProvider");
  }
  return context;
};
